import * as React from "react";
import { observer } from "mobx-react-lite";
import { mk8sDryRun } from "../../../../mobxStores/dryRun/mk8s";
import { Mk8sDraftMobx } from "../../../../mst/stores/mk8s.draft";
import { Mk8sDraftProviderAzureMobx } from "../../../../mst/stores/mk8s.draft.provider.azure";
import { homeLink, request } from "../../../../services/cpln";
import { DryRunAlert } from "../../dryRunAlert";
import { useLocation, useNavigate } from "react-router-dom";
import { NGInput } from "../../../../newcomponents/input/input";
import { NGRadioGroup } from "../../../../newcomponents/radioGroup";
import { NGSelect } from "../../../../newcomponents/select/ngselect";
import { NGButton } from "../../../../newcomponents/button/Button";
import { NGFormElement } from "../../../../newcomponents/ngformelement/ngformelement";
import { useNGFormContext } from "../../../../reactContexts/ngFormContext";
import { NGFormLabel } from "../../../../newcomponents/text/formLabel";
import { NGInputListMst } from "../../../../newcomponents/inputList/inputListMst";
import { getDryRunErrorText } from "../../getDryRunErrorText";
import { mk8sMobx } from "../../../../mst/kinds/mk8s/mk8s";
import { NGProviderBrowser } from "../../ngProviderBrowser";
import { MoreHorizontal } from "react-feather";
import { NGLabel } from "../../../../newcomponents/text/label";
import { NGCheckbox } from "../../../../newcomponents/checkbox";
import NGAlert from "../../../../newcomponents/alert";
import { Instance } from "mobx-state-tree";
import { Mk8sDraftProviderAzureNodePoolsModel } from "../../../../mst/stores/mk8s.draft.provider.azure.nodePool";

interface Props {
  mk8s: mk8sMobx;
  mk8sDraft: Mk8sDraftMobx;
  azure: Mk8sDraftProviderAzureMobx;
  nodePool: Instance<typeof Mk8sDraftProviderAzureNodePoolsModel>;
  index: number;
}
const ProviderAzureNodePoolRaw: React.FC<Props> = ({ mk8sDraft, azure: a, nodePool: np, index }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [browserKey, setBrowserKey] = React.useState("");

  const [recommendedAmis, setRecommendedAmis] = React.useState([]);

  const formData = useNGFormContext();

  const pre = `azure.nodePools[${index}].`;

  React.useEffect(() => {
    fetchRecommendedAmis();
  }, []);

  React.useEffect(() => {
    formData.set(`${pre}bootDiskSize`, { touched: true });
  }, []);

  async function fetchRecommendedAmis() {
    try {
      const { data } = await request({
        service: "api",
        url: homeLink("mk8s") + "/-discover",
        method: "post",
        body: {
          objectType: "recommendedImages",
          cluster: mk8sDraft.asObject,
        },
      });
      const res = data.data.allow.map((r: any) => ({ label: r, value: r }));
      setRecommendedAmis(res);
    } catch (e) {
      setRecommendedAmis([]);
    }
  }

  const zonesDryRun = getDryRunErrorText({
    dryRunResponse: mk8sDryRun.response,
    path: `spec.provider.azure.nodePools[${index}].zones`,
    paths: np.zones.items.map((_, idx) => `spec.provider.azure.nodePools[${index}].zones[${idx}]`),
  });
  const labelsDryRun = getDryRunErrorText({
    dryRunResponse: mk8sDryRun.response,
    path: `spec.provider.azure.nodePools[${index}].labels`,
    paths: np.labels.items.map((i) => `spec.provider.azure.nodePools[${index}].labels.${i.firstValue}`),
  });
  const taintsDryRun = getDryRunErrorText({
    dryRunResponse: mk8sDryRun.response,
    path: `spec.provider.azure.nodePools[${index}].taints`,
    paths: [
      ...np.taints.items.map((_, idx) => `spec.provider.azure.nodePools[${index}].taints[${idx}]`),
      ...np.taints.items.map((_, idx) => `spec.provider.azure.nodePools[${index}].taints[${idx}].key`),
      ...np.taints.items.map((_, idx) => `spec.provider.azure.nodePools[${index}].taints[${idx}].value`),
      ...np.taints.items.map((_, idx) => `spec.provider.azure.nodePools[${index}].taints[${idx}].effect`),
    ],
  });

  return (
    <>
      <div>
        <NGFormElement
          name={`${pre}name`}
          label={np.name.label}
          value={np.name.value}
          onChange={np.name.setValue}
          required
          error={!a.isNodePoolNameValid(np.index) ? "Node pool names must be unique" : ""}
        />

        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}size`}
            label={np.size.label}
            value={np.size.value}
            onChange={np.size.setValue}
            required={np.size.isRequired}
            error={np.size.error}
            innerButtons={[
              {
                title: "Browse",
                onClick: () => setBrowserKey("sizes"),
                render: () => <MoreHorizontal />,
              },
            ]}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={formData.get(`${pre}size`).touched}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.size.setValue}
              path={`spec.provider.azure.nodePools[${index}].size`}
            />
          </div>
        </div>

        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}subnetId`}
            label={np.subnetId.label}
            value={np.subnetId.value}
            onChange={np.subnetId.setValue}
            required={np.subnetId.isRequired}
            error={np.subnetId.error}
            innerButtons={[
              {
                title: "Browse",
                onClick: () => setBrowserKey("subnets"),
                render: () => <MoreHorizontal />,
              },
            ]}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={formData.get(`${pre}subnetId`).touched}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.subnetId.setValue}
              path={`spec.provider.azure.nodePools[${index}].subnetId`}
            />
          </div>
        </div>

        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}minSize`}
            label={np.minSize.label}
            value={np.minSize.value}
            onChange={np.minSize.setValue}
            required={np.minSize.isRequired}
            error={np.minSize.error}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={formData.get(`${pre}minSize`).touched}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.minSize.setValue}
              path={`spec.provider.azure.nodePools[${index}].minSize`}
            />
          </div>
        </div>

        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}maxSize`}
            label={np.maxSize.label}
            value={np.maxSize.value}
            onChange={np.maxSize.setValue}
            required={np.maxSize.isRequired}
            error={np.maxSize.error}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={formData.get(`${pre}maxSize`).touched}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.maxSize.setValue}
              path={`spec.provider.azure.nodePools[${index}].maxSize`}
            />
          </div>
        </div>

        <div className="my-4 p-4 border rounded w-min gap-2" style={{ minWidth: 450 }}>
          <NGFormLabel
            name={
              np.overrideImage.type === "recommended"
                ? `${pre}image.recommended`
                : np.overrideImage.type === "recommended"
                ? `${pre}image.reference`
                : `${pre}image.none`
            }
            // invalid={np.overrideImage.type === "exact" && !np.overrideImage.exact.isValid}
          >
            Override Image
          </NGFormLabel>
          <NGRadioGroup
            className="mb-2"
            options={[
              { label: "None", value: "none" },
              { label: "Recommended", value: "recommended" },
              { label: "Reference", value: "reference" },
            ]}
            value={np.overrideImage.type}
            onChange={(value) => np.overrideImage.setType(value as any)}
          />
          {np.overrideImage.type === "recommended" ? (
            <NGSelect
              name={`${pre}image.recommended`}
              style={{ width: 450, minWidth: 450 }}
              value={np.overrideImage.recommended}
              onChange={np.overrideImage.setRecommended}
              allowEmpty
              options={recommendedAmis}
            />
          ) : np.overrideImage.type === "reference" ? (
            <>
              <NGFormElement
                name={`${pre}image.reference.publisher`}
                label={np.overrideImage.reference_publisher.label}
                value={np.overrideImage.reference_publisher.value}
                onChange={np.overrideImage.reference_publisher.setValue}
                required={np.overrideImage.reference_publisher.isRequired}
                error={np.overrideImage.reference_publisher.error}
              />
              <NGFormElement
                name={`${pre}image.reference.offer`}
                label={np.overrideImage.reference_offer.label}
                value={np.overrideImage.reference_offer.value}
                onChange={np.overrideImage.reference_offer.setValue}
                required={np.overrideImage.reference_offer.isRequired}
                error={np.overrideImage.reference_offer.error}
              />
              <NGFormElement
                name={`${pre}image.reference.sku`}
                label={np.overrideImage.reference_sku.label}
                value={np.overrideImage.reference_sku.value}
                onChange={np.overrideImage.reference_sku.setValue}
                required={np.overrideImage.reference_sku.isRequired}
                error={np.overrideImage.reference_sku.error}
              />
              <NGFormElement
                name={`${pre}image.reference.version`}
                label={np.overrideImage.reference_version.label}
                value={np.overrideImage.reference_version.value}
                onChange={np.overrideImage.reference_version.setValue}
                required={np.overrideImage.reference_version.isRequired}
                error={np.overrideImage.reference_version.error}
              />
            </>
          ) : null}
          <DryRunAlert
            key={mk8sDryRun.key}
            canShow={
              formData.get(`${pre}image.recommended`).touched ||
              formData.get(`${pre}image.reference`).touched ||
              formData.get(`${pre}image.reference.publisher`).touched ||
              formData.get(`${pre}image.reference.offer`).touched ||
              formData.get(`${pre}image.reference.sku`).touched ||
              formData.get(`${pre}image.reference.version`).touched
            }
            dryRunResponse={mk8sDryRun.response}
            path={`spec.provider.azure.nodePools[${index}].overrideImage`}
            paths={[
              `spec.provider.azure.nodePools[${index}].overrideImage.recommended`,
              `spec.provider.azure.nodePools[${index}].overrideImage.reference`,
              `spec.provider.azure.nodePools[${index}].overrideImage.reference.publisher`,
              `spec.provider.azure.nodePools[${index}].overrideImage.reference.offer`,
              `spec.provider.azure.nodePools[${index}].overrideImage.reference.sku`,
              `spec.provider.azure.nodePools[${index}].overrideImage.reference.version`,
            ]}
          />
        </div>
        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}bootDiskSize`}
            label={np.bootDiskSize.label}
            value={np.bootDiskSize.value}
            onChange={np.bootDiskSize.setValue}
            required={np.bootDiskSize.isRequired}
            error={np.bootDiskSize.error}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={formData.get(`${pre}bootDiskSize`).touched}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.bootDiskSize.setValue}
              path={`spec.provider.azure.nodePools[${index}].bootDiskSize`}
            />
          </div>
        </div>

        <div className="mb-4">
          <NGLabel classID="mb-1">Availability Zones</NGLabel>
          <div className="flex items-center">
            {[1, 2, 3].map((zone) => {
              const zones = np.zones.items.map((i) => Number(i.firstValue));
              return (
                <NGCheckbox
                  aria-label={`Zone ${zone}`}
                  style={{ width: 50 }}
                  checked={zones.includes(zone)}
                  onChange={(value) => {
                    if (value) {
                      np.zones.add();
                      const lastItem = np.zones.items[np.zones.items.length - 1];
                      lastItem.setFirstValue(String(zone));
                      return;
                    }

                    const itemToRemove = np.zones.items.find((i) => Number(i.firstValue) === zone);
                    if (itemToRemove) {
                      np.zones.remove(itemToRemove.id);
                    }
                  }}
                >
                  {zone}
                </NGCheckbox>
              );
            })}
            {zonesDryRun.message ? <NGAlert type={zonesDryRun.severity} message={zonesDryRun.message} /> : null}
          </div>
        </div>
        <NGInputListMst
          data={np.labels}
          className="mb-8"
          label="Labels"
          labelRender={() => {
            return (
              <>
                {np.labels.items.some((l) => l.firstValue === "cpln.io/nodeType" && l.secondValue === "core") ? null : (
                  <NGButton
                    variant="primary"
                    className="ml-2"
                    onClick={() => {
                      const id = np.labels.add();
                      const item = np.labels.items.find((i) => i.id === id);
                      if (item) {
                        item.setFirstValue("cpln.io/nodeType");
                        item.setSecondValue("core");
                      }
                    }}
                    size={`small`}
                  >
                    Use this nodepool as BYOK core
                  </NGButton>
                )}
              </>
            );
          }}
          firstLabel="Key"
          firstInput={(i) => {
            let invalid = false;
            if (!i.firstValue) {
              invalid = true;
            }
            if (i.firstValue && labelsDryRun.message.includes(`.${i.firstValue}`)) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.firstValue}
                onChange={(e) => i.setFirstValue(e.target.value)}
              />
            );
          }}
          secondLabel="Value"
          secondInput={(i) => {
            let invalid = false;
            if (!i.secondValue) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.secondValue}
                onChange={(e) => i.setSecondValue(e.target.value)}
              />
            );
          }}
          invalid={labelsDryRun.severity === "error"}
          error={labelsDryRun.severity === "error" ? labelsDryRun.message : ""}
          warning={labelsDryRun.severity === "error" ? "" : labelsDryRun.message}
        />
        <NGInputListMst
          data={np.taints}
          className="mb-8"
          label="Taints"
          firstLabel="Key"
          firstInput={(i, index) => {
            let invalid = false;
            if (!i.firstValue) {
              invalid = true;
            }
            if (i.firstValue && taintsDryRun.message.includes(`[${index}].key`)) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.firstValue}
                onChange={(e) => i.setFirstValue(e.target.value)}
              />
            );
          }}
          secondLabel="Value"
          secondInput={(i) => {
            let invalid = false;
            if (!i.secondValue) {
              invalid = true;
            }
            if (i.secondValue && taintsDryRun.message.includes(`[${index}].value`)) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.secondValue}
                onChange={(e) => i.setSecondValue(e.target.value)}
              />
            );
          }}
          thirdLabel="Effect"
          thirdInput={(i) => {
            let invalid = false;
            if (i.thirdValue && taintsDryRun.message.includes(`[${index}].effect`)) {
              invalid = true;
            }

            return (
              <NGSelect
                invalid={invalid}
                value={i.thirdValue}
                onChange={(value) => i.setThirdValue(value)}
                options={[
                  { label: "NoSchedule", value: "NoSchedule" },
                  { label: "PreferNoSchedule", value: "PreferNoSchedule" },
                  { label: "NoExecute", value: "NoExecute" },
                ]}
              />
            );
          }}
          invalid={taintsDryRun.severity === "error"}
          error={taintsDryRun.severity === "error" ? taintsDryRun.message : ""}
          warning={taintsDryRun.severity === "error" ? "" : taintsDryRun.message}
        />
        <NGButton
          style={{ width: 220 }}
          variant={"danger"}
          outlined
          onClick={() => {
            a.removeNodePoolAt(np.index);
            navigate(`${pathname.split("/-node-pools")[0]}/-node-pools`);
          }}
        >
          Delete Node Pool
        </NGButton>
      </div>
      {browserKey === "sizes" ? (
        <NGProviderBrowser
          width={1550}
          title="Browse Azure Sizes"
          initialValue={np.size.value}
          distinctFilters={["Cores", "Memory", "Arch", "Family", "Tier"]}
          fetchData={async () => {
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: `sizes-${mk8sDraft.provider_azure.location.value}`,
                cluster: mk8sDraft.asObject,
              },
            });
            // {
            //   "CpuArchitectureType": "x64",
            //   "MemoryGB": "32",
            //   "hourlyRate: 0.013608,
            //   "monthlyRate: 9.79776,
            //   "family": "standardBasv2Family",
            //   "name": "Standard_B16als_v2",
            //   "tier": "Standard",
            //   "vCPUs": "16"
            // },
            return data.sort((a: any, b: any) => {
              return a.vCPUs - b.vCPUs || a.MemoryGB - b.MemoryGB || a.name - b.name;
            });
          }}
          onOk={(value) => {
            np.size.setValue(value as string);
          }}
          onClose={() => setBrowserKey("")}
          rightAlign={["Cost per Month"]}
          labels={["Name", "Arch", "Cores", "Memory", "Tier", "Cost per Month"]}
          sortable
          getData={(item: any) => {
            return [
              item.name,
              item.name,
              item.CpuArchitectureType,
              item.vCPUs,
              [item.MemoryGB, `${item.MemoryGB}GB`],
              item.tier,
              [item.monthlyRate || 0, item.monthlyRate ? `$${item.monthlyRate.toFixed(2)}` : `No Cost Info`],
            ];
          }}
        />
      ) : null}
      {browserKey === "subnets" ? (
        <NGProviderBrowser
          title="Browse Azure Subnets"
          initialValue={np.subnetId.value}
          fetchData={async () => {
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "subnets",
                cluster: mk8sDraft.asObject,
              },
            });
            return data;
          }}
          onOk={(value) => np.subnetId.setValue(value as string)}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Resource Group", "Id"]}
          getData={(item: any) => [item.id, item.name, item.resourceGroup, item.id]}
        />
      ) : null}
    </>
  );
};

export const ProviderAzureNodePool = observer(ProviderAzureNodePoolRaw);
